import { useState, useEffect } from 'react';
import * as Yup from "yup";
import { bindActionCreators } from 'redux';
import { connect, useDispatch } from 'react-redux';
import { Formik } from "formik";
import { Link, useNavigate } from 'react-router-dom';
import { WithStyles, withStyles } from '@mui/styles';
import Cookies from 'js-cookie';
import cookie from 'cookie';
import classnames from 'classnames';
import { LDFlagSet } from 'launchdarkly-react-client-sdk';

// Contexts
import { withTenant, withTranslate } from "../../core/TenantProvider";
import withFlags from '../../core/FlagsProvider';
// Components - Atoms, Molecules, Organisms, Pages
import APButton from "../../components/atoms/APButton";
import APHeading from "../../components/atoms/APHeading";
import APInput from "../../components/atoms/APInput";
import APLabel from "../../components/atoms/APLabel";
import APErrorMessage from "../../components/atoms/APErrorMessage";
import APAlertMessage from '../../components/atoms/APAlertMessage';
import APBackdropLoader from "../../components/atoms/APBackdropLoader";
// Files in the same folder
import LoginContainerStyles from "./styles/LoginContainer.style";
// Redux
import { actions as UserActions } from './../../redux/modules/user';
import { ReducerState } from './../../redux/modules';
import { setLoginApiError } from '../../redux/modules/user/actions';
// Types
import { RoleAccessFunction } from '../../core/types/AffiliateDetails';
import { TriggerGoogleAnalytics4Tag, TriggerGoogleAnalyticsTag } from '../../core/utils/GoogleAnalyticTag';
import { GoogleAnalyticTags } from '../../core/utils/GoogleAnalyticTag/GoogleAnalyticsTags';
import { GoogleAnalytics4Tags } from "../../core/utils/GoogleAnalyticTag/GoogleAnalytics4Tags";
import { ReactComponent as SAFlag } from '../../core/assets/CountriesList/countries/south-africa.svg';
import { CDSA_REFERRING_VALUE, PASSWORD_POLICY_ERROR_CODE, TORFX_REFERRING_VALUE } from '../../core/utils/Constants/Constants';
import EncodeData from "../../core/utils/EncodeDecodeData";
import { addUserContext } from '../../core/utils/Coralogix';

declare const window: any;

interface LoginContainerProps extends WithStyles<typeof LoginContainerStyles> {
    authenticate: Function;
    history: any;
    settings: any;
    translate: Function;
    loading: string;
    isAuthenticated: boolean;
    updateTenant: Function;
    tenant: string;
    roleAccessFunction: RoleAccessFunction;
    isLoginApiError: any,
    affiliateDetails: any,
    flags?: LDFlagSet;
}

function LoginContainer(props: LoginContainerProps) {
    const {
        authenticate,
        classes,
        settings: {
            ORG_CODE
        },
        translate,
        loading,
        isAuthenticated,
        updateTenant,
        tenant,
        roleAccessFunction,
        isLoginApiError,
        affiliateDetails,
        flags
    } = props;

    const dispatch = useDispatch();
    const errorMessageTypeMapping = {
        'invalidCred': 'login.form.invalid.credentials',
        'loginFailed': 'login.form.login.failed'
    };
    const [isMessageShowing, setMessageShow] = useState<boolean>(false);
    const [isLoading, setLoading] = useState<boolean>(true);
    const [intervalId, setIntervalId] = useState<number>(-1);
    const { shouldTriggerError, errorType } = isLoginApiError;
    const isLoggedOut = Boolean(sessionStorage.getItem('logged-out')) || false;
    const showSAFlag: boolean = tenant === CDSA_REFERRING_VALUE;
    const { VIEW_ACCOUNT } = roleAccessFunction;

    const referringCompany: string = cookie.parse(document.cookie).cdhCompany;
    const isTorFXOzReferringCompany: boolean = referringCompany === TORFX_REFERRING_VALUE;

    const navigate = useNavigate();

    useEffect(() => {
        const referringCompany = cookie.parse(document.cookie).cdhCompany; if (!referringCompany || referringCompany === 'undefined') {
            let intervalIdValue = window.setInterval(() => {
                let referringCdhCompany = cookie.parse(document.cookie).cdhCompany;
                if (referringCdhCompany && (referringCdhCompany !== 'undefined')) {
                    updateTenant(referringCdhCompany);
                    setIntervalId(intervalIdValue);
                    setLoading(false);
                }
            }, 100);
            setLoading(true);
        } else {
            setLoading(false);
        }
    }, [updateTenant]);

    useEffect(() => {
        if (isAuthenticated && affiliateDetails) {
            const url = window.location.href;
            if (url.includes("/login")) {
                Cookies.remove('Login.Successful');
            }
            if (typeof VIEW_ACCOUNT === 'boolean') {
                navigate(VIEW_ACCOUNT ? '/account' : '/client_management');
            } else {
                navigate('/account');
            }
        }
    }, [isAuthenticated, affiliateDetails])

    if (!isLoading && intervalId) {
        window.clearInterval(intervalId);
    }

    const torFXAccountLinks: () => JSX.Element = () => {
        return (<>
            {translate("login.intro.text.account.info")}
            <a className={classes.accountsTextlink} href={"https://online.torfx.com/CustomerPortal/login.htm"} target="_blank" rel="noopener noreferrer">
                {translate("login.intro.text.personal.account")}
            </a> {translate("login.intro.text.account.connecting.text")}
            <a className={classes.accountsTextlink} href={"https://business.torfx.com/businessportal/login.htm"} target="_blank" rel="noopener noreferrer">
                {translate("login.intro.text.business.account")}
            </a>.
        </>);
    }

    const getErrorMessageType = (error: any) => {
        if (flags?.loginViaApBackend) {
            if (error.status === 401) {
                return errorMessageTypeMapping['invalidCred']
            } else if (error.status === 500) {
                return errorMessageTypeMapping['loginFailed']
            }
            return null;
        }

        if (error.status === 500) {
            return error.response.data.code === 9999
                ? errorMessageTypeMapping['invalidCred']
                : errorMessageTypeMapping['loginFailed'];
        }

        return null;
    };

    const handlePasswordPolicyError = (error: any, email: string) => {
        if (error.status === 400 && error.response.data.code === PASSWORD_POLICY_ERROR_CODE) {
            navigate('/resetpassword', {
                state: {
                    email: email || '',
                    passwordPolicyError: true
                }
            });
            return true;
        }
        return false;
    }

    const handleInvalidCredentialsGoogleAnalytics = (error: any) => {
        if (error.status === 401) {
            TriggerGoogleAnalyticsTag(
                GoogleAnalyticTags.eventAction.load,
                GoogleAnalyticTags.login.load.invalidCredentials
            );

            TriggerGoogleAnalytics4Tag(
                GoogleAnalytics4Tags.login.name,
                GoogleAnalytics4Tags.eventAction.load,
                GoogleAnalytics4Tags.login.load.invalidCredentials
            );
        }
    }

    const AuthenticationTrigger = (values) => {
        addUserContext(values.email, values.email);

        authenticate({
            username: values.email,
            password: EncodeData(values.password),
            orgCode: ORG_CODE
        }).catch((error: any) => {
            if (error.status !== 200) {
                handlePasswordPolicyError(error, values.email);

                handleInvalidCredentialsGoogleAnalytics(error);

                let messageType = getErrorMessageType(error);

                setMessageShow(true);
                dispatch(setLoginApiError({
                    shouldTriggerError: true,
                    errorType: messageType
                }))
            }
        });
    }

    return (loading === 'LOADING') ? <APBackdropLoader open={true} /> : (
        <div>
            <div className={classes.loginContainer}>
                <div className={classnames(classes.loginContent, { [classes.loginContentWithMessage]: (isMessageShowing || isLoggedOut) })}>
                    {shouldTriggerError ?
                        (<APErrorMessage
                            errorMessage={translate(errorType)}
                            onClose={(event) => {
                                event.preventDefault();
                                dispatch(setLoginApiError({
                                    shouldTriggerError: false,
                                    errorType: ''
                                }));
                                setMessageShow(false);
                            }}
                        />) : ("")
                    }

                    {isLoggedOut && !shouldTriggerError ?
                        (<APAlertMessage
                            message={translate("user.logout.alert.message")}
                            title={translate("user.logout.alert.title")}
                            severity={"error"}
                            icon={'error'}
                            onClose={(event) => {
                                event.preventDefault();
                                setMessageShow(false)
                            }}
                        />) : ("")
                    }

                    <div className={classes.loginIntro}>
                        <APHeading className={classes.loginIntroHeader} variant="h4">
                            {translate("login.intro.header")}
                            {showSAFlag && <SAFlag className={classes.loginHeaderSA} />}
                        </APHeading>
                        <APLabel className={classes.loginIntroText}>
                            {translate("login.intro.text")}
                            {isTorFXOzReferringCompany && torFXAccountLinks()}
                        </APLabel>
                    </div>

                    <Formik
                        initialValues={{ email: "", password: "" }}
                        onSubmit={(values, { setSubmitting, resetForm }) => {
                            setSubmitting(false);
                            resetForm();
                            dispatch(setLoginApiError({
                                shouldTriggerError: false,
                                errorType: ''
                            }));
                            setMessageShow(false);
                            AuthenticationTrigger(values);
                        }}
                        validationSchema={Yup.object().shape({
                            email: Yup.string()
                                .email().required(translate("login.form.email.required"))
                                .matches(
                                    /^(([^<>()\],;:\s@]+(\.[^<>()\],;:\s@]+)*)|(.+))@(([^<>()[\],;:\s@]+\.)+[^<>()[\],;:\s@"]{2,})$/,
                                    translate("login.form.email.required")
                                ),
                            password: Yup.string()
                                .required(translate("login.form.password.required"))
                                .min(8, translate("login.form.password.minimumLength"))
                                .max(16, translate("login.form.password.maximumLength"))
                                .matches(
                                    /^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.{8,})/,
                                    translate("login.form.password.requirements")
                                ),
                        })}
                    >
                        {(propsAttributes) => {
                            const {
                                values,
                                touched,
                                errors,
                                dirty,
                                isValid,
                                handleChange,
                                handleBlur,
                                handleSubmit,
                            } = propsAttributes;

                            return (
                                <div className={classes.loginForm}>
                                    <form onSubmit={handleSubmit}>
                                        <div className={classes.inputsSurround}>
                                            <APInput
                                                type="email"
                                                label={translate("login.form.email.label")}
                                                name="email"
                                                className={classes.loginInput}
                                                error={errors.email && touched.email}
                                                helperText={errors.email && touched.email && translate('login.form.email.error')}
                                                defaultValue={values.email}
                                                onChange={handleChange}
                                                onBlur={handleBlur}
                                                inputProps={{ tabIndex: "1" }}
                                            // isCoralogixMasked={true}
                                            />
                                            <div className={classes.inputWrapper}>
                                                <APInput
                                                    type="password"
                                                    label={translate("login.form.password.label")}
                                                    name="password"
                                                    className={classes.loginInput}
                                                    error={errors.password && touched.password}
                                                    helperText={touched.password && errors.password}
                                                    defaultValue={values.password}
                                                    onChange={handleChange}
                                                    onBlur={handleBlur}
                                                    inputProps={{ tabIndex: "2" }}
                                                    supportsUnmasking={true}
                                                    isCoralogixMasked={true}
                                                />
                                            </div>
                                        </div>
                                        <Link to="/resetpassword"
                                            onClick={() => {
                                                TriggerGoogleAnalyticsTag(
                                                    GoogleAnalyticTags.eventAction.click,
                                                    GoogleAnalyticTags.login.click.forgotPasswordLink
                                                );
                                                TriggerGoogleAnalytics4Tag(
                                                    GoogleAnalytics4Tags.login.name,
                                                    GoogleAnalytics4Tags.eventAction.click,
                                                    GoogleAnalytics4Tags.login.click.forgotPasswordLink
                                                );
                                            }}
                                            className={classes.link}>
                                            {translate("login.form.forgot.password.link")}
                                        </Link>
                                        <div className={classes.loginActions}>
                                            <APButton
                                                className={classes.loginButton}
                                                type="submit"
                                                variant="contained"
                                                color="primary"
                                                disabled={!(isValid && dirty)}
                                                // onClick={submitForm}
                                                onClick={() => {
                                                    TriggerGoogleAnalyticsTag(
                                                        GoogleAnalyticTags.eventAction.click,
                                                        GoogleAnalyticTags.login.click.CTA
                                                    );
                                                    TriggerGoogleAnalytics4Tag(
                                                        GoogleAnalytics4Tags.login.name,
                                                        GoogleAnalytics4Tags.eventAction.click,
                                                        GoogleAnalytics4Tags.login.click.CTA
                                                    );
                                                }}
                                                tabIndex={3}
                                                disableRipple={true}
                                                id="loginButton"
                                            >
                                                {translate("login.action.login.label")}
                                            </APButton>
                                        </div>
                                    </form>
                                </div>
                            );
                        }}
                    </Formik>
                </div>
            </div>
        </div>
    );
}

const mapStateToProps = (store: ReducerState) => ({
    loading: store.user.loadingState,
    isAuthenticated: store.user.isAuthenticated,
    roleAccessFunction: store.user.roleAccessFunction,
    affiliateDetails: store.user.affiliateDetails,
    isLoginApiError: store.user.isLoginApiError
});

const mapDispatchToProps = (dispatch: any) => bindActionCreators({
    authenticate: UserActions.authenticate
}, dispatch);

export default connect(mapStateToProps, mapDispatchToProps)(
    withFlags(
        withTenant(
            withTranslate(
                withStyles(LoginContainerStyles)(
                    LoginContainer
                )
            )
        )
    )
);
